"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, Droplets, Wrench, ShieldCheck, ArrowRight, CircleCheck, Search, MessageSquare as MessageSquareCheck, Truck, PhoneCall, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const waterHeaterBrands = [{
  id: "wh-1",
  name: "Rheem",
  category: "Gas, Electric & Hybrid Units",
  popular: true,
  link1Text: "Water Heater Repair",
  link1Url: "/rheem-water-heater-service/",
  link2Text: "Gas Water Heater Service",
  link2Url: "/gas-water-heater-service/",
  link3Text: "Electric Water Heater Service",
  link3Url: "/electric-water-heater-service/"
}, {
  id: "wh-2",
  name: "Bradford White",
  category: "Residential & Commercial Systems",
  popular: true,
  link1Text: "Water Heater Repair",
  link1Url: "/bradford-white-water-heater-service/",
  link2Text: "Gas Water Heater Service",
  link2Url: "/gas-water-heater-service/",
  link3Text: "Electric Water Heater Service",
  link3Url: "/electric-water-heater-service/"
}, {
  id: "wh-3",
  name: "A. O. Smith",
  category: "High-Efficiency Storage & Tankless",
  popular: false,
  link1Text: "Water Heater Repair",
  link1Url: "/a-o-smith-water-heater-service/",
  link2Text: "",
  link2Url: "",
  link3Text: "",
  link3Url: ""
}, {
  id: "wh-4",
  name: "Navien",
  category: "Condensing Tankless & Combi-Boilers",
  popular: false,
  link1Text: "Water Heater Repair",
  link1Url: "/navien-water-heater-service/",
  link2Text: "Tankless Water Heater Service",
  link2Url: "/tankless-water-heater-service/",
  link3Text: "",
  link3Url: ""
}, {
  id: "wh-5",
  name: "Rinnai",
  category: "Continuous Flow Tankless Systems",
  popular: false,
  link1Text: "Water Heater Repair",
  link1Url: "/rinnai-water-heater-service/",
  link2Text: "Tankless Water Heater Service",
  link2Url: "/tankless-water-heater-service/",
  link3Text: "",
  link3Url: ""
}];
const fixtureBrands = [{
  id: "fix-1",
  name: "Moen",
  category: "Faucets, Valves & Smart Flow",
  popular: true,
  link1Text: "Faucet & Sink Repair",
  link1Url: "/moen-faucet-sink-repair/",
  link2Text: "Fixture Problems",
  link2Url: "/fixture-problems/"
}, {
  id: "fix-2",
  name: "Delta",
  category: "Kitchen, Bath & Shower Cartridges",
  popular: true,
  link1Text: "Faucet & Sink Repair",
  link1Url: "/delta-faucet-sink-repair/",
  link2Text: "Fixture Problems",
  link2Url: "/fixture-problems/"
}, {
  id: "fix-3",
  name: "Kohler",
  category: "Toilets, Flushometers & Basins",
  popular: true,
  link1Text: "Toilet Repair",
  link1Url: "/kohler-toilet-repair/",
  link2Text: "Toilet Problems",
  link2Url: "/toilet-problems/"
}, {
  id: "fix-4",
  name: "American Standard",
  category: "Vitreous China & High-Efficiency Toilets",
  popular: false,
  link1Text: "Toilet Repair",
  link1Url: "/american-standard-toilet-repair/",
  link2Text: "Toilet Problems",
  link2Url: "/toilet-problems/"
}];
const serviceSteps = [{
  id: "step-1",
  stepNumber: "01",
  title: "Identify Your Brand",
  description: "Tell us the make, model number, or serial code on your water heater tank, fixture, or valve."
}, {
  id: "step-2",
  stepNumber: "02",
  title: "Tell Us The Issue",
  description: "Describe symptoms such as sudden leaks, no hot water, low pressure, pilot lights out, or running toilets."
}, {
  id: "step-3",
  stepNumber: "03",
  title: "Dispatched With OEM-Spec Parts",
  description: "Our Jonesboro emergency truck arrives loaded with factory-grade cartridges, elements, burners, and valves."
}];
export default function Brands() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="brands-we-service" data-nav="light" className="bg-card text-card-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-semibold tracking-wide uppercase mb-4">
            <ShieldCheck className="size-4 text-primary" />
            <span>Certified Brand Diagnostics</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            Plumbing & Water Heater Brands We Service in Jonesboro
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            Our licensed technicians carry genuine OEM-specification components and factory diagnostic procedures for all major residential and commercial plumbing manufacturers.
          </p>
        </div>

        {/* ZONE 1: Water Heater Brands Subsection */}
        <div className="mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-6 pb-3 border-b border-border">
            <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0">
              <Flame className="size-5" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground font-default">
                Water Heater Brands
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                Dedicated repair and replacement pathways for industry-leading water heating systems
              </p>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {waterHeaterBrands.map((brand, i) => <motion.div key={brand.id} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-background border border-border hover:border-primary/50 transition-[border-color,box-shadow,background-color] duration-200 shadow-xs hover:shadow-md p-5 rounded-xl flex flex-col justify-between group">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary bg-secondary px-2.5 py-1 rounded-md">
                        Water Heating
                      </span>
                      {brand.popular && <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground">
                          <Sparkles className="size-3 text-accent" />
                          Most Common
                        </span>}
                    </div>
                    <h4 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors font-default">
                      {brand.name}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 font-sans">
                      {brand.category}
                    </p>
                  </div>

                  {/* Mini link chain */}
                  <div className="mt-5 pt-4 border-t border-border/70 flex flex-col gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Service Direct Links:
                    </span>
                    <div className="flex flex-col gap-1.5">
                      {brand.link1Url && <Link to={brand.link1Url} className="inline-flex items-center justify-between text-xs font-semibold text-primary hover:text-accent-foreground hover:bg-secondary/60 px-2 py-1.5 rounded-md transition-colors">
                          <span>{brand.link1Text}</span>
                          <ArrowRight className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        </Link>}
                      {brand.link2Url && <Link to={brand.link2Url} className="inline-flex items-center justify-between text-xs font-semibold text-primary hover:text-accent-foreground hover:bg-secondary/60 px-2 py-1.5 rounded-md transition-colors">
                          <span>{brand.link2Text}</span>
                          <ArrowRight className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        </Link>}
                      {brand.link3Url && <Link to={brand.link3Url} className="inline-flex items-center justify-between text-xs font-semibold text-primary hover:text-accent-foreground hover:bg-secondary/60 px-2 py-1.5 rounded-md transition-colors">
                          <span>{brand.link3Text}</span>
                          <ArrowRight className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        </Link>}
                    </div>
                  </div>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* ZONE 2: Plumbing Fixture Brands Subsection */}
        <div className="mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-6 pb-3 border-b border-border">
            <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0">
              <Droplets className="size-5" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground font-default">
                Plumbing Fixture Brands
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                Original valve cartridges, toilet rebuild kits, and precision sink repairs
              </p>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {fixtureBrands.map((brand, i) => <motion.div key={brand.id} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-background border border-border hover:border-primary/50 transition-[border-color,box-shadow,background-color] duration-200 shadow-xs hover:shadow-md p-5 rounded-xl flex flex-col justify-between group">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary bg-secondary px-2.5 py-1 rounded-md">
                        Fixtures
                      </span>
                      {brand.popular && <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground">
                          Popular
                        </span>}
                    </div>
                    <h4 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors font-default">
                      {brand.name}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 font-sans">
                      {brand.category}
                    </p>
                  </div>

                  {/* Mini link chain */}
                  <div className="mt-5 pt-4 border-t border-border/70 flex flex-col gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Service Direct Links:
                    </span>
                    <div className="flex flex-col gap-1.5">
                      {brand.link1Url && <Link to={brand.link1Url} className="inline-flex items-center justify-between text-xs font-semibold text-primary hover:text-accent-foreground hover:bg-secondary/60 px-2 py-1.5 rounded-md transition-colors">
                          <span>{brand.link1Text}</span>
                          <ArrowRight className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        </Link>}
                      {brand.link2Url && <Link to={brand.link2Url} className="inline-flex items-center justify-between text-xs font-semibold text-primary hover:text-accent-foreground hover:bg-secondary/60 px-2 py-1.5 rounded-md transition-colors">
                          <span>{brand.link2Text}</span>
                          <ArrowRight className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        </Link>}
                    </div>
                  </div>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* ZONE 3: How Brand Service Works & CTA */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 md:p-10 lg:p-12 shadow-lg relative overflow-hidden">
          <div className="max-w-3xl mb-8 md:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-bold uppercase tracking-wider mb-3">
              <Wrench className="size-3.5" />
              <span>Simple 3-Step Protocol</span>
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-foreground font-default tracking-tight">
              How Brand Service Works
            </h3>
            <p className="mt-2 text-sm md:text-base text-primary-foreground/80 font-sans leading-relaxed">
              We streamline troubleshooting so your exact manufacturer hardware is serviced without trial and error.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {serviceSteps.map((step, idx) => <div key={step.id} data-index={idx} className="bg-primary-foreground/10 border border-primary-foreground/15 rounded-xl p-5 sm:p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-extrabold text-accent font-default">
                      {step.stepNumber}
                    </span>
                    <div className="size-8 rounded-full bg-primary-foreground/15 flex items-center justify-center text-primary-foreground">
                      {idx === 0 ? <Search className="size-4 text-accent" /> : idx === 1 ? <MessageSquareCheck className="size-4 text-accent" /> : <Truck className="size-4 text-accent" />}
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-primary-foreground font-default mb-2">
                    {step.title}
                  </h4>
                  <p className="text-xs md:text-sm text-primary-foreground/85 font-sans leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Action Row */}
          <div className="pt-6 border-t border-primary-foreground/20 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-xs md:text-sm text-primary-foreground/90 font-medium">
              <CircleCheck className="size-5 text-accent shrink-0" />
              <span>Full compliance with manufacturer warranty specifications.</span>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button asChild className="w-full sm:w-auto bg-accent text-accent-foreground font-bold hover:brightness-105 rounded-full px-6 py-3 shadow-md transition-[filter,transform] active:scale-[0.98]">
                <Link to="/contact/">
                  <span>Request Service</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <a href="tel:4708479076" className="hidden md:inline-flex items-center gap-2 text-xs font-bold text-primary-foreground hover:text-accent transition-colors px-3 py-2" aria-label="Direct dispatch phone call">
                <PhoneCall className="size-3.5 text-accent" />
                <span>(470) 847-9076</span>
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>;
}